<!--
  Students and families: each one's rate, plan and how long they've been with you, hours left
  (as a bar), what they owe, and the few things you do for a student: schedule a session (on the
  calendar), renew their prepaid hours (paid now or later), or record a payment.
-->
<script lang="ts">
  import {
    hoursText,
    isSeeking,
    lessonProgress,
    pairingPay,
    studentSummary,
    tenure,
    type TutoringStudent,
  } from "@split/core";
  import { fill, shows } from "../../settings/index.ts";
  import * as TT from "../../settings/tutoring.ts";
  import Requests from "./Requests.svelte";
  import { tutoring } from "../../lib/tutoring.svelte.ts";
  import StudentCard from "./StudentCard.svelte";
  import { link } from "../../lib/router.svelte.ts";
  import { app } from "../../lib/store.svelte.ts";
  import { centsInput, formatCents, readMoney, shortDate, today } from "../../lib/format.ts";

  let q = $state("");
  let open = $state<string | null>(null);
  let adding = $state(false);
  let editing = $state<TutoringStudent | null>(null);

  const list = $derived(
    tutoring.students
      .filter((s) => !s.archived)
      .filter((s) => !q.trim() || `${s.name} ${s.family} ${s.className}`.toLowerCase().includes(q.trim().toLowerCase()))
      .map((s) => ({ s, sum: studentSummary(s.id, tutoring.entries), prog: lessonProgress(s.id, tutoring.entries, tutoring.slots) })),
  );
  const owed = $derived(list.reduce((t, x) => t + Math.max(0, x.sum.balanceCents), 0));

  // ---- add / edit a student
  const blank = () => ({ seeking: false, market: "", planHours: "10", planCash: "", planCard: "", first: "", last: "", tutorId: "", family: "", contact: "", className: "", since: today(), cash: "", card: "", grandfathered: false, billing: "hourly" as TutoringStudent["billing"], note: "", userId: "" });
  let f = $state(blank());
  function startEdit(s: TutoringStudent) {
    editing = s;
    adding = true;
    f = { seeking: !!s.seeking, market: s.marketPayCents ? centsInput(s.marketPayCents) : "", planHours: s.planMinutes ? String(s.planMinutes / 60) : "10", planCash: s.planCents ? centsInput(s.planCents) : "", planCard: s.planCardCents ? centsInput(s.planCardCents) : "", first: s.name.split(" ")[0] ?? "", last: s.name.split(" ").slice(1).join(" "), tutorId: s.tutorId ?? "", family: s.family, contact: s.contact, className: s.className, since: s.since, cash: centsInput(s.rateCashCents), card: s.rateCardCents ? centsInput(s.rateCardCents) : "", grandfathered: s.grandfathered, billing: s.billing, note: s.note, userId: s.userId ?? "" };
  }
  async function save(e: SubmitEvent) {
    e.preventDefault();
    const cash = readMoney(f.cash || "0");
    const card = f.card.trim() ? readMoney(f.card) : null;
    if (cash === null || (f.card.trim() && card === null)) return app.toast("Enter rates like 40", "error");
    const planHours = Number(f.planHours) || 0;
    const planCash = f.planCash.trim() ? readMoney(f.planCash) : Math.round(planHours * cash);
    const planCard = f.planCard.trim() ? readMoney(f.planCard) : Math.round(planHours * (card ?? cash));
    if (f.billing === "package" && (!(planHours > 0) || planCash === null || planCard === null)) return app.toast("Enter the hours and price for their plan", "error");
    const market = f.market.trim() ? readMoney(f.market) : 0;
    if (market === null) return app.toast(TT.STUDENT_MARKET, "error");
    const body = {
      name: `${f.first.trim()} ${f.last.trim()}`.trim(),
      tutorId: f.tutorId || null,
      family: f.family.trim(),
      contact: f.contact.trim(),
      className: f.className.trim(),
      since: f.since || today(),
      rateCashCents: cash,
      rateCardCents: card ?? 0,
      grandfathered: f.grandfathered,
      billing: f.billing,
      planMinutes: f.billing === "package" ? Math.round(planHours * 60) : 0,
      planCents: f.billing === "package" ? (planCash ?? 0) : 0,
      planCardCents: f.billing === "package" ? (planCard ?? 0) : 0,
      note: f.note.trim(),
      userId: f.userId || null,
      seeking: f.seeking && !f.tutorId,
      marketPayCents: market,
    };
    const ok = editing ? (await tutoring.update("students", editing.id, body, "Saved"), true) : !!(await tutoring.add("students", body, `Added ${body.name}`));
    if (ok) {
      adding = false;
      editing = null;
      f = blank();
    }
  }

  // ---- what you do for one student: renew their hours, record a payment, or an other charge
  let act = $state<"renew" | "payment" | "item" | null>(null);
  let a = $state({ hours: "10", cents: "", byCard: false, paidNow: true, on: today(), method: "zelle" as "cash" | "zelle" | "card" | "other", note: "" });
  const planPrice = (s: TutoringStudent, hours: number, byCard: boolean) => {
    if (s.planMinutes && Math.round(hours * 60) === s.planMinutes) return byCard ? s.planCardCents || s.planCents || 0 : s.planCents || 0;
    return Math.round(hours * (byCard ? s.rateCardCents || s.rateCashCents : s.rateCashCents));
  };
  function startAct(kind: typeof act, s: TutoringStudent) {
    act = kind;
    const hours = s.planMinutes ? s.planMinutes / 60 : 10;
    a = { hours: String(hours), cents: kind === "renew" ? centsInput(planPrice(s, hours, false)) : "", byCard: false, paidNow: true, on: today(), method: "zelle", note: "" };
  }
  function repriced(s: TutoringStudent) {
    a.cents = centsInput(planPrice(s, Number(a.hours) || 0, a.byCard));
  }
  async function doAct(s: TutoringStudent) {
    if (act === "renew") {
      const hours = Number(a.hours);
      const cents = a.cents.trim() ? readMoney(a.cents) : 0;
      if (!(hours > 0) || cents === null) return app.toast("Enter the hours and the price", "error");
      await tutoring.add("entries", { kind: "purchase", studentId: s.id, on: a.on, minutes: Math.round(hours * 60), cents, method: a.byCard ? "card" : "", note: `${hours} hours` });
      if (a.paidNow && cents) await tutoring.add("entries", { kind: "payment", studentId: s.id, on: a.on, cents, method: a.byCard ? "card" : a.method, note: `For ${hours} hours` });
      app.toast(a.paidNow && cents ? `${hours} hours added, paid ${formatCents(cents)}` : `${hours} hours added`);
    } else if (act === "payment") {
      const cents = readMoney(a.cents);
      if (!cents) return app.toast("Enter the amount", "error");
      await tutoring.add("entries", { kind: "payment", studentId: s.id, on: a.on, cents, method: a.method, note: a.note }, "Payment recorded");
    } else if (act === "item") {
      const cents = readMoney(a.cents);
      if (!cents || !a.note.trim()) return app.toast("Say what it is and the price", "error");
      await tutoring.add("entries", { kind: "item", studentId: s.id, on: a.on, cents, note: a.note.trim() }, "Added");
    }
    act = null;
  }
  // ---- a tutor's pay for this student (per student and tutor; minimum wage until set)
  let pairing = $state<string | null>(null);
  /** Tutors with a pay set for this student, and their usual tutor. */
  const pairsOf = (s: TutoringStudent) =>
    tutoring.tutors.filter((t) => !t.archived && (t.id === s.tutorId || tutoring.pairings.some((p) => p.studentId === s.id && p.tutorId === t.id)));
  let pr = $state({ tutorId: "", pay: "", past: false });
  function startPair(s: TutoringStudent) {
    const tutorId = s.tutorId ?? tutoring.tutors.find((t) => !t.archived)?.id ?? "";
    pr = { tutorId, pay: tutorId ? centsInput(pairingPay(s.id, tutorId, tutoring.pairings)) : "", past: false };
    pairing = s.id;
  }
  async function savePair(s: TutoringStudent) {
    const cents = readMoney(pr.pay);
    if (!pr.tutorId || cents === null) return app.toast(TT.PAIR_PAY, "error");
    if (await tutoring.setPairing(s.id, pr.tutorId, cents, pr.past, TT.PAIR_SAVED)) pairing = null;
  }
  const month = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

  const METHOD: Record<string, string> = { zelle: "Zelle", cash: "Cash", card: "Card", other: "Other" };
  const KIND: Record<string, string> = { session: "Session", purchase: "Hours bought", payment: "Payment", item: "Bought", adjust: "Adjustment", payout: "Payout" };
</script>

<div class="bar">
  <input class="input" type="search" placeholder="Search students or families" bind:value={q} aria-label="Search students" />
  <span class="muted">{list.length} {list.length === 1 ? "student" : "students"}{owed ? `, ${formatCents(owed)} owed` : ""}</span>
  {#if !adding}<button class="btn primary" onclick={() => { adding = true; editing = null; f = blank(); }}>Add a student</button>{/if}
</div>

{#if adding}
  <form class="panel form" onsubmit={save}>
    <h2>{editing ? `Edit ${editing.name}` : "Add a student"}</h2>
    <div class="grid">
      <label class="field"><span>First name</span><input class="input" bind:value={f.first} placeholder="Emily" autocomplete="off" required /></label>
      <label class="field"><span>Last name</span><input class="input" bind:value={f.last} placeholder="Chen" autocomplete="off" required /></label>
      <label class="field"><span>Family or parent</span><input class="input" bind:value={f.family} placeholder="Chen family" /></label>
      <label class="field"><span>Contact</span><input class="input" bind:value={f.contact} placeholder="Phone or email" /></label>
      <label class="field"><span>Member since</span><input class="input" type="date" bind:value={f.since} /></label>
      <label class="field"><span>Rate, Zelle or cash ($/hr)</span><input class="input money" bind:value={f.cash} inputmode="decimal" placeholder="40" /></label>
      <label class="field"><span>Rate, card ($/hr)</span><input class="input money" bind:value={f.card} inputmode="decimal" placeholder="45" /></label>
      <label class="field"
        ><span>How they pay</span>
        <select class="input" bind:value={f.billing}>
          <option value="hourly">Per session</option>
          <option value="package">Prepaid hours</option>
          <option value="monthly">Monthly</option>
        </select></label
      >
      {#if f.billing === "package"}
        <label class="field"><span>Hours each renewal</span><input class="input" bind:value={f.planHours} inputmode="decimal" placeholder="10" /></label>
        <label class="field"><span>Price, Zelle or cash</span><input class="input money" bind:value={f.planCash} inputmode="decimal" placeholder={centsInput(Math.round((Number(f.planHours) || 0) * (readMoney(f.cash || "0") ?? 0)))} /></label>
        <label class="field"><span>Price, card</span><input class="input money" bind:value={f.planCard} inputmode="decimal" placeholder={centsInput(Math.round((Number(f.planHours) || 0) * (readMoney(f.card || f.cash || "0") ?? 0)))} /></label>
      {/if}
      <label class="field"
        ><span>Their account (family sees their own view)</span>
        <select class="input" bind:value={f.userId}>
          <option value="">Not linked</option>
          {#each tutoring.accounts as acc (acc.id)}<option value={acc.id}>{acc.name}</option>{/each}
        </select></label
      >
      <label class="field"
        ><span>Usually taught by</span>
        <select class="input" bind:value={f.tutorId}>
          <option value="">You</option>
          {#each tutoring.tutors.filter((t) => !t.archived) as t (t.id)}<option value={t.id}>{t.name}</option>{/each}
        </select></label
      >
      <label class="field"><span>Group class (optional)</span><input class="input" bind:value={f.className} placeholder="SAT group" /></label>
    </div>
    <label class="check"><input type="checkbox" bind:checked={f.grandfathered} /> Keeps an older rate (gold "member since" badge)</label>
    {#if !f.tutorId}<label class="check"><input type="checkbox" bind:checked={f.seeking} /> {TT.STUDENT_SEEKING}</label>{/if}
    <label class="field"><span>{TT.STUDENT_MARKET}</span><input class="input money" bind:value={f.market} inputmode="decimal" placeholder="" /></label>
    <label class="field"><span>Notes</span><input class="input" bind:value={f.note} placeholder="Subjects, goals, schedule" /></label>
    <div class="btns">
      <button class="btn primary" disabled={!f.first.trim() || !f.last.trim()}>Save</button>
      <button type="button" class="btn quiet" onclick={() => { adding = false; editing = null; }}>Cancel</button>
      {#if editing}<button type="button" class="btn quiet" onclick={() => editing && tutoring.update("students", editing.id, { archived: true }, "Archived").then(() => (adding = false))}>Archive</button>{/if}
    </div>
  </form>
{/if}

<Requests />
<div class="list">
  {#each list as { s, sum, prog } (s.id)}
    <StudentCard
      name={s.name}
      family={s.family}
      badge={s.grandfathered ? `Member since ${month(s.since)}` : ""}
      badgeTitle={s.grandfathered ? `Keeps an older rate; with you ${tenure(s.since, app.today)}` : ""}
      parts={[
        `${formatCents(s.rateCashCents)}/hr Zelle or cash`,
        s.rateCardCents && s.rateCardCents !== s.rateCashCents ? `${formatCents(s.rateCardCents)}/hr card` : "",
        s.grandfathered ? "" : `Member since ${month(s.since)}`,
        s.billing === "package" && s.planMinutes ? `${s.planMinutes / 60} hours for ${formatCents(s.planCents ?? 0)}` : "",
        s.className,
        s.tutorId ? `with ${tutoring.tutors.find((t) => t.id === s.tutorId)?.name ?? "a tutor"}` : "",
        s.userId ? (s.linkConfirmed ? "family on the site" : "waiting for the family to confirm") : "",
        isSeeking(s) ? TT.STUDENT_SEEKING_TAG : "",
        s.marketPayCents ? fill(TT.STUDENT_MARKET_TAG, { pay: `${formatCents(s.marketPayCents)}/hr` }) : "",
      ]}
      prog={sum.boughtMinutes ? prog : null}
      usedNoPrepaidMinutes={!sum.boughtMinutes && s.billing === "package" ? sum.usedMinutes : 0}
      balanceCents={sum.balanceCents}
      expanded={open === s.id}
      ontoggle={() => (open = open === s.id ? null : s.id)}
    >
        <div class="detail">
          <div class="acts">
            <a class="btn small primary" href="/tutoring/calendar?student={s.id}" use:link>Schedule a session</a>
            {#if s.billing === "package"}<button class="btn small" onclick={() => startAct("renew", s)}>Renew {s.planMinutes ? `${s.planMinutes / 60} hours` : "hours"}</button>{/if}
            <button class="btn small" onclick={() => startAct("payment", s)}>Record a payment</button>
            <button class="btn small quiet" onclick={() => startEdit(s)}>Edit</button>
            <button class="link-btn small" onclick={() => startAct("item", s)}>Other charge</button>
          </div>
          <Requests studentId={s.id} />
          {#if shows(TT.PAIR_TITLE)}
            <div class="pairs">
              <strong class="small">{TT.PAIR_TITLE}</strong>
              {#each pairsOf(s) as t (t.id)}
                {@const set = tutoring.pairings.some((p) => p.studentId === s.id && p.tutorId === t.id)}
                <span class="small">{fill(TT.PAIR_LINE, { tutor: t.name, pay: set ? `${formatCents(pairingPay(s.id, t.id, tutoring.pairings))}/hr` : fill(TT.PAIR_DEFAULT, { pay: `${formatCents(pairingPay(s.id, t.id, []))}/hr` }) })}</span>
              {/each}
              <button class="link-btn small" onclick={() => startPair(s)}>{TT.PAIR_SET}</button>
            </div>
            {#if pairing === s.id}
              <form class="act" onsubmit={(e) => { e.preventDefault(); void savePair(s); }}>
                <label class="field"><span>{TT.PAIR_TUTOR}</span>
                  <select class="input" bind:value={pr.tutorId} onchange={() => (pr.pay = centsInput(pairingPay(s.id, pr.tutorId, tutoring.pairings)))}>
                    {#each tutoring.tutors.filter((t) => !t.archived) as t (t.id)}<option value={t.id}>{t.name}</option>{/each}
                  </select></label>
                <label class="field"><span>{TT.PAIR_PAY}</span><input class="input money" inputmode="decimal" bind:value={pr.pay} /></label>
                <label class="check"><input type="checkbox" bind:checked={pr.past} /> {TT.PAIR_PAST}</label>
                <button class="btn primary">{TT.CAL_EDIT_SAVE}</button>
                <button type="button" class="btn quiet" onclick={() => (pairing = null)}>{TT.CAL_EDIT_CANCEL}</button>
              </form>
            {/if}
          {/if}
          {#if act}
            <form class="act" onsubmit={(e) => { e.preventDefault(); void doAct(s); }}>
              <label class="field"><span>Date</span><input class="input" type="date" bind:value={a.on} /></label>
              {#if act === "renew"}
                <label class="field"><span>Hours</span><input class="input" inputmode="decimal" bind:value={a.hours} oninput={() => repriced(s)} /></label>
                <label class="field"><span>Price</span><input class="input money" inputmode="decimal" bind:value={a.cents} /></label>
                <label class="check"><input type="checkbox" bind:checked={a.byCard} onchange={() => repriced(s)} /> By card</label>
                <label class="check"><input type="checkbox" bind:checked={a.paidNow} /> Paid now</label>
                {#if a.paidNow && !a.byCard}
                  <label class="field"
                    ><span>How</span>
                    <select class="input" bind:value={a.method}><option value="zelle">Zelle</option><option value="cash">Cash</option><option value="other">Other</option></select></label
                  >
                {/if}
              {:else if act === "payment"}
                <label class="field"><span>Amount</span><input class="input money" inputmode="decimal" bind:value={a.cents} placeholder="0.00" /></label>
                <label class="field"
                  ><span>How</span>
                  <select class="input" bind:value={a.method}>
                    <option value="zelle">Zelle</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select></label
                >
              {:else}
                <label class="field"><span>What</span><input class="input" bind:value={a.note} placeholder="SAT book" /></label>
                <label class="field"><span>Price</span><input class="input money" inputmode="decimal" bind:value={a.cents} placeholder="0.00" /></label>
              {/if}
              {#if act !== "item"}<label class="field grow"><span>Note</span><input class="input" bind:value={a.note} /></label>{/if}
              <button class="btn primary">Save</button>
              <button type="button" class="btn quiet" onclick={() => (act = null)}>Cancel</button>
            </form>
          {/if}
          <dl class="nums">
            <div><dt>Sessions</dt><dd>{sum.sessions}{sum.lastSession ? `, last ${shortDate(sum.lastSession)}` : ""}</dd></div>
            {#if sum.boughtMinutes}<div><dt>Prepaid</dt><dd>{hoursText(sum.boughtMinutes)} bought, {hoursText(sum.usedMinutes)} used</dd></div>{/if}
            <div><dt>Charged</dt><dd>{formatCents(sum.chargedCents)}</dd></div>
            <div><dt>Paid</dt><dd>{formatCents(sum.paidCents)}</dd></div>
          </dl>
          <ul class="hist">
            {#each tutoring.entries.filter((e) => e.studentId === s.id).slice().reverse() as e (e.id)}
              <li>
                <span>{shortDate(e.on)} · {KIND[e.kind]}{e.minutes ? ` · ${hoursText(e.minutes)}` : ""}{e.tutorId ? ` · ${tutoring.tutors.find((t) => t.id === e.tutorId)?.name ?? ""}` : ""}{e.method ? ` · ${METHOD[e.method] ?? e.method}` : ""}{e.note ? ` · ${e.note}` : ""}</span>
                <span class="num">{e.cents ? (e.kind === "payment" ? `−${formatCents(e.cents)}` : formatCents(e.cents)) : ""}</span>
                <button class="x" aria-label="Remove" title="Remove" onclick={() => tutoring.remove("entries", e.id)}>×</button>
              </li>
            {:else}
              <li class="muted">Nothing yet.</li>
            {/each}
          </ul>
        </div>
    </StudentCard>
  {:else}
    <div class="empty">{q ? "No students match." : "No students yet. Add your families one at a time, with the rate you charge each."}</div>
  {/each}
</div>

<style>
  .bar {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .bar .input {
    max-width: 280px;
  }

  .bar .btn {
    margin-left: auto;
  }

  .panel {
    padding: 16px;
    margin-bottom: 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
    display: grid;
    gap: 10px;
  }

  .panel h2 {
    margin: 0;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
  }

  .btns,
  .acts {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .detail {
    display: grid;
    gap: 12px;
    padding: 0 16px 16px;
  }

  .act {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: flex-end;
    padding: 12px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .act .field {
    flex: 0 1 150px;
  }

  .act .grow {
    flex: 1 1 200px;
  }

  .pairs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    align-items: baseline;
  }

  .small {
    font-size: var(--t-xs);
  }

  .nums {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
    margin: 0;
  }

  .nums dt {
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .nums dd {
    margin: 0;
  }

  .hist {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: var(--t-sm);
  }

  .hist li {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 5px 0;
    border-top: 1px solid var(--rule);
  }

  .hist li span:first-child {
    flex: 1;
  }

  .x {
    border: 0;
    background: none;
    color: var(--muted);
    cursor: pointer;
    font-size: 1rem;
  }
</style>
